import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';

function GovernanceAudit() {
  return (
    <>
      <SEO pathname="/agents/governance-audit" agentKey="GovernanceAudit" />

    <main className="agent-page" style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--color-bg-light)', color: 'var(--color-text-primary)' }}>
      {/* Hero Section */}
      <section style={{ padding: '60px 0', background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, transparent 100%)' }}>
        <div className="container">
          <div style={{ marginBottom: '2rem' }}>
            <Link to="/" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span>←</span> Back to Home
            </Link>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '48px', alignItems: 'center' }}>
            <div className="waas-card" style={{ flex: '1', minWidth: '300px', maxWidth: '400px', margin: '0', background: 'white', padding: '40px', borderRadius: '24px', boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: 'var(--color-primary)', filter: 'blur(80px)', opacity: '0.2', borderRadius: '50%' }}></div>
              <div className="waas-icon-container" style={{ margin: '0 auto 24px auto', width: '140px', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-subtle)', borderRadius: '50%' }}>
                 <img src="/images/icons/logo_security.webp" alt="Governance Audit Agent" width="100" height="100" style={{ objectFit: 'contain' }} />
              </div>
              <h1 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '8px', color: '#1d1d1f' }}>Governance Audit Agent</h1>
              <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', marginBottom: '24px', fontSize: '1.1rem' }}>Shift-Left Infrastructure Scanner</p>
              
              <div style={{ background: 'rgba(0,200,83,0.1)', color: '#00c853', padding: '8px 16px', borderRadius: '100px', display: 'inline-block', fontWeight: '600', fontSize: '0.9rem', width: '100%', textAlign: 'center', marginBottom: '16px' }}>
                ● Status: Online & Scanning
              </div>
              <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#86868b', marginBottom: '32px' }}>Enterprise Data Policy Standardization</p>
              
              <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Governance%20Audit%20Agent" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', textAlign: 'center', display: 'block', padding: '16px 0', fontSize: '1.1rem', textDecoration: 'none' }}>
                Request Access Now
              </a>
            </div>

            <div style={{ flex: '2', minWidth: '400px' }}>
              <span className="section-label">Digital Workforce Persona</span>
              <h2 style={{ fontSize: '3rem', marginBottom: '24px', color: '#1d1d1f', fontWeight: '700', lineHeight: '1.2' }}>Your Data Governance Authority.</h2>
              <p style={{ fontSize: '1.25rem', color: '#424245', marginBottom: '24px', lineHeight: '1.6' }}>
                Auditing massive legacy infrastructures is painstaking. This agent programmatically scans large-scale data environments against rigorous Shift-Left framework principles across your pipeline tiers.
              </p>
              <p style={{ fontSize: '1.1rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                It evaluates PII (Personally Identifiable Information) masking flaws, missing constraints, suboptimal clustering allocations costing you money on full-scans, and data residency violations. It groups its absolute deterministic findings into severity tiers (Essential, Recommended, Good To Have) — flagging blockers accurately without hallucinating hypothetical issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Capabilities Section */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span className="section-label">Agent Capabilities</span>
            <h2 className="section-title">Comprehensive Audit Topologies</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px' }}>
            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Cross-Tier Provenance Checking</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Automatically cross-verifies pipeline health natively from Bronze to Silver to Gold. If a Silver table has mapped PII but is tagged as "internal", or if Bronze tables possess retention times too short to support a Silver replay recovery, the agent detects and issues critical alerts.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>String Misuse Detection</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Dynamically crawls columns hunting for silent partition-pruning failures. For example: flagging explicit columns stored as STRING instead of TIMESTAMP or DECIMAL—preventing wasted overhead computation and degraded aggregations down the line. 
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Silver/Gold Change Validations</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Checks schemas requiring `MERGE` conditions. Without proper `business_key_hash` or `payload_hash` designations on target tables, change data capture executes brutal full-table scan comparisons. The agent points these architecture blunders out directly.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Regulation & FinOps Compliance</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Flags missing `cost_center` tags causing chargeback blind spots. Additionally hunts for data residency configuration omissions to ensure strict compliance (GDPR/FedRAMP) laws are not silently bypassed due to missing location assignments.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>ACID Transaction Assurance</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Asserts modern storage compliance natively across disparate frameworks (ensuring tables in BigQuery are not arbitrarily `EXTERNAL`, Snowflake structures possess Fail-Safe guarantees instead of `TRANSIENT`, and Databricks operates accurately on `DELTA`).
              </p>
            </div>
            
            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Prioritized Traffic Lighting</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Renders a flawless three-tier output architecture report. Explicit red findings (ESSENTIAL failures breaking production safely), amber warnings (RECOMMENDED infrastructure improvements affecting scale/cost), and blue diagnostics (shift-left maturity observations).
              </p>
            </div>
          </div>
        </div>
      </section>
      <FAQSection agentKey="GovernanceAudit" />
    </main>
    </>
  );
}

export default GovernanceAudit;
